
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AuthUser } from "./types";
import { useAuthFunctions } from "./useAuthFunctions";
import { queryClient } from "@/hooks/subscriptions/queryClient";

export const useAuthState = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const authFunctions = useAuthFunctions(setUser, setIsLoading);

  // Check subscription status after login
  const checkSubscriptionStatus = useCallback(async () => {
    try {
      await supabase.functions.invoke('check-subscription');
      // Invalidate subscription queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['user-subscription'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-features'] });
      queryClient.invalidateQueries({ queryKey: ['subscription-access'] });
      queryClient.invalidateQueries({ queryKey: ['custom-plan-info'] });
    } catch (error) {
      console.error('Error checking subscription status:', error);
    }
  }, []);
  
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || "",
            name: session.user.user_metadata.name || session.user.email?.split("@")[0] || "",
            companyName: session.user.user_metadata.companyName || "",
          });
          
          authFunctions.fetchUserRoles(session.user.id);
          
          // Check subscription on login
          if (event === 'SIGNED_IN') {
            checkSubscriptionStatus();
          }
        } else {
          setUser(null);
        }
        setIsLoading(false);
        setError(null);
      }
    );

    checkUser();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const checkUser = async () => {
    try {
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        throw sessionError;
      }
      
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email || "",
          name: session.user.user_metadata.name || session.user.email?.split("@")[0] || "",
          companyName: session.user.user_metadata.companyName || "",
        });
        
        authFunctions.fetchUserRoles(session.user.id);
        
        // Check subscription on initial load
        checkSubscriptionStatus();
      }
      setError(null);
    } catch (err) {
      console.error("Error checking user session:", err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    user,
    setUser,
    isLoading,
    setIsLoading,
    error,
    setError,
    ...authFunctions
  };
};
